import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import {
  getAdminRetailerDetail,
  getAdminRetailerStats,
  getAdminRetailerActivity,
} from "../../services/api";

export default function AdminRetailerActivity() {
  const { retailerId } = useParams();
  const [retailer, setRetailer] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  console.log("activity: ", activity);

  useEffect(() => {
    fetchData();
  }, [retailerId]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("admin_token");

      const [retailerRes, statsRes, activityRes] = await Promise.all([
        getAdminRetailerDetail(retailerId, token),
        getAdminRetailerStats(retailerId, token),
        getAdminRetailerActivity(retailerId, token),
      ]);

      if (retailerRes && retailerRes.success) {
        setRetailer(retailerRes.retailer);
      }
      if (statsRes && statsRes.success) {
        const statsData = statsRes.stats || statsRes;
        setStats(statsData);
      } else if (statsRes) {
        const statsData = statsRes.stats || statsRes;
        setStats(statsData);
      }
      if (activityRes && activityRes.success) {
        setActivity(activityRes.activity || []);
      } else if (activityRes) {
        setActivity(activityRes.activity || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getActivityTypeColor = (type) => {
    const colors = {
      login: "bg-green-100 text-green-800",
      logout: "bg-gray-100 text-gray-800",
      product_added: "bg-blue-100 text-blue-800",
      product_updated: "bg-yellow-100 text-yellow-800",
      product_deleted: "bg-red-100 text-red-800",
      status_updated: "bg-purple-100 text-purple-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const ACTION_LABELS = {
    retailer_login: "Logged In",
    retailer_logout: "Logged Out",
    retailer_signup: "Account Created",
    product_added: "Product Added",
    product_updated: "Product Updated",
    product_deleted: "Product Deleted",
    size_added: "Size Added",
    size_updated: "Size Updated",
    size_deleted: "Size Deleted",
    logo_uploaded: "Logo Uploaded",
    profile_updated: "Profile Updated",
    visualizer_accessed: "Visualizer Accessed",
  };

  // 2. Badge color based on action
  function getActionColor(action) {
    const colors = {
      retailer_login: "bg-green-100 text-green-700",
      retailer_signup: "bg-blue-100 text-blue-700",
      retailer_logout: "bg-gray-100 text-gray-600",
      product_added: "bg-green-100 text-green-700",
      product_updated: "bg-yellow-100 text-yellow-700",
      product_deleted: "bg-red-100 text-red-700",
      size_added: "bg-green-100 text-green-700",
      size_updated: "bg-yellow-100 text-yellow-700",
      size_deleted: "bg-red-100 text-red-700",
      logo_uploaded: "bg-purple-100 text-purple-700",
      profile_updated: "bg-yellow-100 text-yellow-700",
      visualizer_accessed: "bg-orange-100 text-orange-700",
    };
    return colors[action] || "bg-gray-100 text-gray-600";
  }

  // 3. Badge color + label for target_type (your "Activity Type" column)
  function getTargetTypeBadge(target_type) {
    const config = {
      retailer: { label: "Auth", color: "bg-blue-100 text-blue-700" },
      product: { label: "Product", color: "bg-green-100 text-green-700" },
      size: { label: "Size", color: "bg-purple-100 text-purple-700" },
      profile: { label: "Profile", color: "bg-yellow-100 text-yellow-700" },
      visualizer: {
        label: "Visualizer",
        color: "bg-orange-100 text-orange-700",
      },
    };
    return (
      config[target_type] || {
        label: target_type || "-",
        color: "bg-gray-100 text-gray-600",
      }
    );
  }

  // 4. Build a meaningful description from action + metadata
  //    This is the key fix — your current code reads log.description
  //    which doesn't exist. The data lives in log.metadata.
  function getDescription(action, metadata) {
    if (!metadata) return "-";

    switch (action) {
      case "retailer_login":
      case "retailer_logout":
      case "retailer_signup":
        // metadata: { email, company_name }
        return metadata.email
          ? `${metadata.email}${metadata.company_name ? ` (${metadata.company_name})` : ""}`
          : metadata.company_name || "-";

      case "product_added":
      case "product_updated":
      case "product_deleted":
        // metadata: { sku, category, surface_type, product_size, application_type, image_count }
        return [
          metadata.sku,
          metadata.category,
          metadata.surface_type,
          metadata.product_size,
        ]
          .filter(Boolean)
          .join(" · ");
      // e.g. "WALL-57BC75F4EC · wallpaper · textured · 800x1600"

      case "size_added":
      case "size_updated":
      case "size_deleted":
        return metadata.name || metadata.size_name || "-";

      case "logo_uploaded":
        return metadata.filename || "Logo file uploaded";

      case "profile_updated":
        return metadata.changed_fields
          ? `Changed: ${metadata.changed_fields.join(", ")}`
          : "Profile updated";

      case "visualizer_accessed":
        return metadata.token
          ? `Token: ${String(metadata.token).slice(0, 8)}...`
          : "Visualizer link opened";

      default:
        // Fallback: show first non-null value from metadata
        const firstVal = Object.values(metadata).find((v) => v != null);
        return firstVal ? String(firstVal) : "-";
    }
  }

  // 5. "2 hours ago" formatter
  function timeAgo(isoString) {
    if (!isoString) return "-";
    const diff = Math.floor(
      (Date.now() - new Date(isoString).getTime()) / 1000,
    );
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  return (
    <div className="flex">
      <AdminSidebar />

      <main className="flex-1 ml-64 p-8">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/retailers"
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <h2 className="text-2xl font-bold text-gray-900">
              Retailer Activity Log
            </h2>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {retailer && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Retailer Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">
                        {retailer.name || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">
                        {retailer.email || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Company Name</p>
                      <p className="font-medium text-gray-900">
                        {retailer.company_name || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          retailer.status === "active"
                            ? "bg-green-100 text-green-800"
                            : retailer.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {retailer.status || "-"}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Business Type</p>
                      <p className="font-medium text-gray-900">
                        {retailer.business_type || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Material Type</p>
                      <p className="font-medium text-gray-900">
                        {retailer.material_type || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">
                        Total Products
                      </span>
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.total_products || stats.products || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">All products</p>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">Total Tiles</span>
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                        />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.total_tiles || stats.tiles || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">Tile products</p>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-500">
                        Total Wallpapers
                      </span>
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">
                      {stats.total_wallpapers || stats.wallpapers || 0}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Wallpaper products
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Activity Log
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Action
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        IP Address
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activity.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          No activity found
                        </td>
                      </tr>
                    ) : (
                      activity.map((log) => {
                        const typeBadge = getTargetTypeBadge(log.target_type);
                        return (
                          <tr key={log.id} className="hover:bg-gray-50">
                            {/* Action — colored badge + human label */}
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}
                              >
                                {ACTION_LABELS[log.action] || log.action}
                              </span>
                            </td>

                            {/* Type — target_type as badge */}
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeBadge.color}`}
                              >
                                {typeBadge.label}
                              </span>
                            </td>

                            {/* Description — extracted from metadata */}
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {getDescription(log.action, log.metadata)}
                            </td>

                            {/* IP Address */}
                            <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                              {log.ip_address || "-"}
                            </td>

                            {/* Time */}
                            <td
                              className="px-6 py-4 text-sm text-gray-500"
                              title={log.created_at}
                            >
                              {timeAgo(log.created_at)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
